import React, { useState } from 'react';
import axios from 'axios';
import { Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InquiryForm = ({
  defaultSubject = '',
  inquiryType = 'general',
  defaultDetails = {},
  title = 'Send Us an Inquiry',
  subtitle = 'Our travel experts will get back to you within 24 hours',
  compact = false,
  showTypeSelector = false
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: defaultSubject,
    inquiry_type: inquiryType,
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        ...formData,
        details: defaultDetails
      });
      toast.success('Inquiry submitted! Our team will contact you shortly.');
      setFormData({ name: '', email: '', phone: '', subject: defaultSubject, inquiry_type: inquiryType, message: '' });
    } catch (err) {
      toast.error('Failed to submit. Please call +91 90169 30091 directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="inquiry-form" className={compact ? '' : 'bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-gray-100'}>
      {!compact && (
        <div className="mb-6">
          <h3 className="text-2xl font-bold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="if-name">Full Name *</Label>
            <Input id="if-name" data-testid="if-name" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="if-phone">Phone *</Label>
            <Input id="if-phone" data-testid="if-phone" required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="if-email">Email *</Label>
            <Input id="if-email" data-testid="if-email" type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
          </div>
          {showTypeSelector ? (
            <div>
              <Label htmlFor="if-type">Service Type</Label>
              <Select value={formData.inquiry_type} onValueChange={(v) => setFormData({ ...formData, inquiry_type: v })}>
                <SelectTrigger id="if-type" data-testid="if-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="visa">Visa Services</SelectItem>
                  <SelectItem value="tour">Tour Package</SelectItem>
                  <SelectItem value="flight">Flight Booking</SelectItem>
                  <SelectItem value="hotel">Hotel Booking</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div>
              <Label htmlFor="if-subject">Subject</Label>
              <Input id="if-subject" data-testid="if-subject" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} />
            </div>
          )}
        </div>
        <div>
          <Label htmlFor="if-message">Message *</Label>
          <Textarea id="if-message" data-testid="if-message" required rows={4} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} placeholder="Tell us about your travel plans, dates, number of travelers..." />
        </div>
        <Button type="submit" data-testid="if-submit" disabled={submitting} className="w-full bg-[#2D8DB4] hover:bg-[#247a9a] h-12 text-base">
          {submitting ? 'Submitting...' : (<>Submit Inquiry <Send className="ml-2" size={16} /></>)}
        </Button>
        <p className="text-xs text-center text-gray-500">
          Or call us at <a href="tel:+919016930091" className="text-[#2D8DB4] font-semibold">+91 90169 30091</a>
        </p>
      </form>
    </div>
  );
};

export default InquiryForm;
